import React from 'react';
import axios, { AxiosInstance } from 'axios';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Modal,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Cached as CachedIcon,
  Download as DownloadIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
  GridToolbar,
} from '@mui/x-data-grid-pro';
import { useAuth } from 'context/auth-provider';

interface JobRecord {
  metadata?: Record<string, unknown>;
  initiated_by?: string;
  status?: string;
  ttl?: string;
  start_time?: string;
  end_time?: string;
  job_id: string;
  job_type?: string;
}

interface JobsApiResponse {
  jobs?: JobRecord[];
  items?: JobRecord[];
}

const formatDateTime = (value?: string): string => {
  if (!value) return '--';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

const formatExpiry = (ttl?: string): string => {
  if (!ttl) return '--';
  const epochSeconds = Number(ttl);
  if (!Number.isFinite(epochSeconds)) return ttl;
  return new Date(epochSeconds * 1000).toLocaleString();
};

const parseJobs = (payload: JobsApiResponse): JobRecord[] => {
  if (Array.isArray(payload.jobs)) return payload.jobs;
  if (Array.isArray(payload.items)) return payload.items;
  return [];
};

const isCompletedJob = (status?: string): boolean =>
  (status || '').trim().toUpperCase() === 'COMPLETED';

export default function JobsView() {
  const theme = useTheme();
  const { user } = useAuth();

  const axiosInstance = React.useRef<AxiosInstance | null>(null);
  const loadAbortController = React.useRef<AbortController | null>(null);
  const downloadAbortController = React.useRef<AbortController | null>(null);

  const [jobs, setJobs] = React.useState<JobRecord[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = React.useState<string | null>(null);
  const [selectedMetadata, setSelectedMetadata] = React.useState<Record<string, unknown> | null>(null);
  const [downloadingJobId, setDownloadingJobId] = React.useState<string | null>(null);
  const hasLoadedJobsRef = React.useRef<boolean>(false);

  const triggerBrowserDownload = (downloadUrl: string): void => {
    // Use direct navigation to presigned S3 URL. This avoids browser CORS checks
    // that apply to fetch/XHR from the app origin.
    console.log('Triggering browser download for URL:', downloadUrl);
    window.open(downloadUrl, '_blank', 'noopener,noreferrer');
  };

  const initAxiosInstance = React.useCallback(() => {
    if (!user || axiosInstance.current) return;

    axiosInstance.current = axios.create({
      baseURL: process.env.REACT_APP_AMA_V2_API_URL,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: user.idToken,
      },
    });
  }, [user]);

  const loadJobs = React.useCallback(async (): Promise<void> => {
    if (!user) {
      setError('User not authenticated');
      return;
    }

    initAxiosInstance();
    setLoading(true);
    setError(null);

    // Cancel any in-flight list request and start a fresh one.
    loadAbortController.current?.abort();
    loadAbortController.current = new AbortController();

    console.log('JobsView.loadJobs :: starting request', {
      isSignalAborted: loadAbortController.current.signal.aborted,
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await axiosInstance.current!.get<JobsApiResponse>('/jobs', {
        signal: loadAbortController.current.signal,
      });

      setJobs(parseJobs(response.data));
      setLastUpdated(new Date().toLocaleString());
    } catch (err: unknown) {
      console.log('JobsView.loadJobs :: request failed', {
        isCancel: axios.isCancel(err),
        isSignalAborted: loadAbortController.current?.signal.aborted,
        code: axios.isAxiosError(err) ? err.code : undefined,
        message: axios.isAxiosError(err) ? err.message : String(err),
        error: err,
      });
      if (axios.isCancel(err)) return;
      if (axios.isAxiosError(err)) {
        const message =
          (typeof err.response?.data?.message === 'string' && err.response?.data?.message) || err.message;
        setError(message);
      } else {
        setError('Failed to load scheduled jobs');
      }
    } finally {
      setLoading(false);
    }
  }, [initAxiosInstance, user]);

  const handleDownloadClick = React.useCallback(async (job: JobRecord): Promise<void> => {
    if (!user) {
      setError('User not authenticated');
      return;
    }

    if (!job.job_id) {
      setError('Unable to download: missing job id');
      return;
    }

    initAxiosInstance();
    setError(null);
    setDownloadingJobId(job.job_id);

    // Cancel any in-flight download-link request and start a fresh one.
    downloadAbortController.current?.abort();
    downloadAbortController.current = new AbortController();

    console.log('JobsView.handleDownloadClick :: starting request', {
      jobId: job.job_id,
      isSignalAborted: downloadAbortController.current.signal.aborted,
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await axiosInstance.current!.get('/get-download-url', {
        params: { jobId: job.job_id },
        signal: downloadAbortController.current.signal,
      });

      const data = response?.data || {};
      const downloadUrl =
        (typeof data.download_url === 'string' && data.download_url) ||
        (typeof data.downloadUrl === 'string' && data.downloadUrl) ||
        (typeof data.url === 'string' && data.url) ||
        '';

      if (!downloadUrl) {
        setError('Download URL was not returned by the server');
        return;
      }

      triggerBrowserDownload(downloadUrl);
    } catch (err: unknown) {
      console.log('JobsView.handleDownloadClick :: request failed', {
        jobId: job.job_id,
        isCancel: axios.isCancel(err),
        isSignalAborted: downloadAbortController.current?.signal.aborted,
        code: axios.isAxiosError(err) ? err.code : undefined,
        message: axios.isAxiosError(err) ? err.message : String(err),
        error: err,
      });
      if (axios.isCancel(err)) return;
      if (axios.isAxiosError(err)) {
        const message =
          (typeof err.response?.data?.message === 'string' && err.response?.data?.message) || err.message;
        setError(message);
      } else {
        setError('Failed to fetch download URL');
      }
    } finally {
      setDownloadingJobId(null);
    }
  }, [initAxiosInstance, user]);

  const handleRefreshButtonClick = React.useCallback(async () => {
    await loadJobs();
  }, [loadJobs]);

  React.useEffect(() => {
    if (!user || hasLoadedJobsRef.current) return;
    hasLoadedJobsRef.current = true;
    loadJobs();
  }, [user, loadJobs]);

  React.useEffect(() => {
    return () => {
      console.log('JobsView.useEffect cleanup :: aborting controllers', {
        loadWasAlreadyAborted: loadAbortController.current?.signal.aborted,
        downloadWasAlreadyAborted: downloadAbortController.current?.signal.aborted,
        timestamp: new Date().toISOString(),
      });
      loadAbortController.current?.abort();
      downloadAbortController.current?.abort();
    };
  }, []);

  const CustomFooter = () => (
    <Container sx={{ height: 40 }}>
      <Grid container justifyContent="space-between" alignItems="center" sx={{ height: '100%', color: theme.palette.text.secondary }}>
        <Grid item xs={4}>
          <Typography variant="body2" align="left">
            Records: {jobs.length}
          </Typography>
        </Grid>
        <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Typography variant="body2" align="center" sx={{ display: 'flex', alignItems: 'center', color: theme.palette.text.secondary }}>
            <a
              href="#"
              aria-label="Refresh Jobs List"
              aria-describedby="jobs_refresh_timestamp"
              title="Refresh Jobs List"
              onClick={(e) => { e.preventDefault(); handleRefreshButtonClick(); }}
            >
              <CachedIcon
                sx={{
                  verticalAlign: 'middle',
                  mr: 1,
                  mb: 0.3,
                  color: theme.palette.text.disabled,
                  animation: loading ? 'spin 1s linear infinite' : 'none',
                }}
              />
            </a>
            <span id="jobs_refresh_timestamp"><span className="visually-hidden">Last Refreshed: </span>{lastUpdated || '--'}</span>
          </Typography>
        </Grid>
        <Grid item xs={4}>
          &nbsp;
        </Grid>
      </Grid>
    </Container>
  );

  const columns: Array<GridColDef> = [
    { field: 'job_id', headerName: 'Job Id', flex: 1, minWidth: 300, sortable: true },
    { field: 'job_type', headerName: 'Job Type', flex: 0.8, minWidth: 180, sortable: true },
    { field: 'status', headerName: 'Status', flex: 0.6, minWidth: 120, sortable: true },
    { field: 'initiated_by', headerName: 'Initiated By', flex: 0.8, minWidth: 100, sortable: true },
    {
      field: 'start_time',
      headerName: 'Start Time',
      flex: 0.9,
      minWidth: 160,
      sortable: true,
      renderCell: (params: GridRenderCellParams<JobRecord>) => formatDateTime(params.row.start_time),
      sortComparator: (a, b) => {
        const left = new Date(a || '').getTime();
        const right = new Date(b || '').getTime();
        return (Number.isNaN(left) ? 0 : left) - (Number.isNaN(right) ? 0 : right);
      },
    },
    {
      field: 'end_time',
      headerName: 'End Time',
      flex: 0.9,
      minWidth: 160,
      sortable: true,
      renderCell: (params: GridRenderCellParams<JobRecord>) => formatDateTime(params.row.end_time),
      sortComparator: (a, b) => {
        const left = new Date(a || '').getTime();
        const right = new Date(b || '').getTime();
        return (Number.isNaN(left) ? 0 : left) - (Number.isNaN(right) ? 0 : right);
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 90,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<JobRecord>) => (
        params.row.job_type === 'pdf_to_ppt_conversion' && isCompletedJob(params.row.status) ? (
          <Tooltip title="Download">
            <IconButton
              aria-label="Download"
              disabled={downloadingJobId === params.row.job_id}
              onClick={() => handleDownloadClick(params.row)}
              size="small"
              sx={{ color: theme.palette.grey[700] }}
            >
              {downloadingJobId === params.row.job_id ? (
                <CircularProgress size={16} />
              ) : (
                <DownloadIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        ) : (
          
            <IconButton
              aria-label="Job"
              size="small"
              sx={{ color: theme.palette.grey[700] }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
        
        )
      ),
    },
    {
      field: 'ttl',
      headerName: 'Expiry',
      flex: 0.9,
      minWidth: 190,
      sortable: true,
      renderCell: (params: GridRenderCellParams<JobRecord>) => formatExpiry(params.row.ttl),
      sortComparator: (a, b) => {
        const left = Number(a);
        const right = Number(b);
        return (Number.isFinite(left) ? left : 0) - (Number.isFinite(right) ? right : 0);
      },
    },
  ];

  return (
    <Grid item xs={12} sx={{ position: 'relative', height: '100%' }}>
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography component="h2" variant="h6" color="primary" sx={{ flexGrow: 1 }}>
            Scheduled Jobs
          </Typography>
          <Tooltip title="Refresh jobs">
            <IconButton
              aria-label="Refresh jobs list"
              onClick={loadJobs}
              sx={{ color: theme.palette.primary.main }}
            >
              <CachedIcon
                sx={{
                  animation: loading ? 'spin 1s linear infinite' : 'none',
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: 'rgba(255, 255, 255, 0.6)',
                zIndex: 2,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <DataGridPro
            rows={jobs.map((job, index) => ({ ...job, id: job.job_id || `${index}` }))}
            columns={columns}
            getRowId={(row) => row.id}
            disableColumnSelector
            disableDensitySelector
            pagination
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25, page: 0 } },
              sorting: { sortModel: [{ field: 'start_time', sort: 'desc' }] },
            }}
            slots={{ toolbar: GridToolbar, footer: () => (
              <Stack justifyContent="center" alignItems="center">
                <CustomFooter />
              </Stack>
            ) }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                printOptions: { disableToolbarButton: true },
                csvOptions: { disableToolbarButton: true },
              },
            }}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'
            }
            sx={{
              minHeight: 300,
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
              },
              '& .MuiDataGrid-row.Mui-even .MuiDataGrid-cell': {
                backgroundColor: theme.palette.grey[100],
              },
              '& .MuiDataGrid-row:hover .MuiDataGrid-cell': {
                backgroundColor: theme.palette.grey[300],
              },
            }}
          />
        </Box>
      </Paper>

      <Modal
        open={Boolean(selectedMetadata)}
        onClose={() => setSelectedMetadata(null)}
        aria-labelledby="jobs-metadata-title"
        aria-describedby="jobs-metadata-content"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', md: 640 },
            maxHeight: '80vh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.grey[300]}`,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Typography id="jobs-metadata-title" variant="h6" sx={{ mb: 2 }}>
            Job Metadata
          </Typography>
          <Box
            id="jobs-metadata-content"
            component="pre"
            sx={{
              m: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'monospace',
              fontSize: '0.85rem',
            }}
          >
            {JSON.stringify(selectedMetadata || {}, null, 2)}
          </Box>
        </Box>
      </Modal>
    </Grid>
  );
}
